import { defineStore } from 'pinia'
import { getPromotions } from '~/composables/api'
import { STORE_ID } from '~/constants/store' // not reactive, cannot destructure
import { useRestaurantStore } from '~/store'
import type { PromotionsState as State } from '~/types/promotions'
import { isExpectedApiNetworkControlError } from '~/utils/apiError'

const initialState = (): State => ({
  promotions: [],
  fetching: {
    promotions: false
  },
  fetchDidFail: {
    promotions: false
  },
  lastPresentedProminentPromotionId: undefined,
  lastUpdated: {
    promotions: undefined
  },
  lastFailed: {
    promotions: undefined
  }
})

/**
 * @name usePromotionsStore
 * @description Store for promotions displayed across the app
 *
 */
const usePromotionsStore = defineStore(STORE_ID.PROMOTIONS, {
  state: initialState,

  getters: {},

  actions: {
    fetchPromotions: async function () {
      const minimumPromosToShow = 3
      if (this.fetching.promotions) {
        return
      }
      this.fetching.promotions = true
      const restaurantStore = useRestaurantStore()

      try {
        // If restaurant is a demo restaurant, do not send restaurantId
        const restaurantId = !restaurantStore.isDemoRestaurant ? restaurantStore.getSelectedRestaurant?.id : undefined

        const res = await getPromotions(restaurantId, restaurantStore.orderConfig?.orderType)
        if (res.promotions !== undefined && res.promotions !== null) {
          const promos = res.promotions
          if (promos.length > 0) {
            // Ensure that at least 3 promotions are shown
            const countPromosToAdd = minimumPromosToShow - promos.length
            if (countPromosToAdd > 0) {
              for (let i = 0; i < countPromosToAdd; i++) {
                promos.push(res.promotions[0])
              }
            }
          }
          this.promotions = promos
        } else {
          this.promotions = []
        }
        this.fetchDidFail.promotions = false
        this.lastFailed.promotions = undefined
        this.lastUpdated.promotions = Date.now()
      } catch (error: any) {
        // promotions are cleared on error
        if (!isExpectedApiNetworkControlError(error)) {
          console.error('Error fetching promotions', error)
        }
        this.clearPromotions()
        this.fetchDidFail.promotions = true
        this.lastFailed.promotions = Date.now()
      } finally {
        this.fetching.promotions = false
      }
    },
    clearPromotions: function () {
      this.promotions = []
      this.fetching.promotions = false
      this.fetchDidFail.promotions = false
    }
  },
  persist: {
    pick: ['lastPresentedProminentPromotionId']
  }
})

export default usePromotionsStore
export { initialState }
